<template>
    <pendo-layout-preview
        v-if="!loading"
        :layout="processedLayout" />
    <pendo-loading-indicator
        v-else
        :loading="loading"
        size="large" />
    <pendo-tag
        v-if="layout.recommended"
        :label="$t('layoutChooser.Recommended')"
        :color="recommendedTagColor"
        class="pendo-layout-grid__recommended-label"
        round />
</template>

<script>
import { PendoTag, PendoLayoutPreview, PendoLoadingIndicator } from '@pendo/components';
import { BuildingBlock, BuildingBlockEmails, BuildingBlockLayouts, BuildingBlockPropertiesService, normalizePreview } from '@pendo/services/BuildingBlocks';
import carouselThumbnail from '@img/mobile-carousel-thumbnail.png';
import get from 'lodash/get';
import { APPS } from '@pendo/services/Constants';
import { isMobileApp } from '@/utils/apps';
const { createBuildingBlocksProperties } = BuildingBlockPropertiesService;
const { EMAIL_PLATFORM } = APPS;

export default {
    name: 'LayoutChooserPreviewCard',
    components: {
        PendoTag,
        PendoLayoutPreview,
        PendoLoadingIndicator,
    },
    props: {
        getThemeById: {
            type: Function,
            required: true,
        },
        isAnnouncement: {
            type: Boolean,
            required: false,
            default: false,
        },
        isEmailCreation: {
            type: Boolean,
            required: false,
            default: false,
        },
        layout: {
            type: Object,
            required: true,
        },
        recommendedTagColor: {
            type: String,
            required: true,
        },
        selectedAppPlatform: {
            type: String,
            required: true,
        },
        defaultThemeForSelectedApp: {
            type: Object,
            required: true,
        },
    },
    data () {
        return {
            loading: true,
            processedLayout: null,
        };
    },
    computed: {
        isMobileApp () {
            return isMobileApp(this.selectedAppPlatform);
        },
    },
    created () {
        this.processedLayout = this.processLayout(this.layout);
    },
    methods: {
        generateBuildingBlockProperties (layout) {
            const isDefaultLayout = layout.origin === 'default';
            const isCustomLayout = layout.origin === 'custom';
            const layoutSteps = BuildingBlockLayouts.getLayoutOrSteps(layout);

            // For now, we only render a preview for the first step of a multi-step layout.
            const layoutStep = layoutSteps[0];

            const clonedBuildingBlocks = structuredClone(layoutStep.buildingBlocks);
            let buildingBlocks = createBuildingBlocksProperties({
                buildingBlocks: clonedBuildingBlocks,
                platform: this.isEmailCreation ? EMAIL_PLATFORM : this.selectedAppPlatform,
                shouldCallWidgetIdTransform: !!isCustomLayout,
            });
            if (isDefaultLayout) {
                // default layout specific processing
                const { htmlTemplate, textTemplate } = this.isEmailCreation
                    ? BuildingBlockEmails.buildingBlocksToHtml(buildingBlocks)
                    : {};

                return {
                    ...layout,
                    buildingBlocks,
                    ...(this.isEmailCreation && { htmlTemplate, textTemplate }),
                };
            }

            if (isCustomLayout) {
                // custom layout specific processing
                const isPendoDefaultLayout = layout.id.includes('_PENDO_DEFLYT');
                const localTheme = isPendoDefaultLayout ? this.defaultThemeForSelectedApp : this.getThemeById(layoutStep.themeId);
                const isEmbeddedLayout = BuildingBlockLayouts.isEmbeddedLayout(layout);

                if (this.isAnnouncement) {
                    buildingBlocks = clonedBuildingBlocks;
                } else {
                    buildingBlocks = BuildingBlock.applyThemeToBuildingBlocks({
                        theme: localTheme,
                        buildingBlocks: clonedBuildingBlocks,
                        options: {
                            isEmail: this.isEmailCreation,
                            isEmbed: isEmbeddedLayout,
                        },
                    });
                }

                let options = {};

                // if guide has numberScaleGroup - then buildingBlocksToDom function need to get the
                // width of the container in order to display the thumbnail right
                if (this.isMobileApp && BuildingBlockLayouts.isNumberScalePoll({ buildingBlocks })) {
                    options = { width: 250, ignoreMargins: true, isThumbnail: true };
                }

                const dom = BuildingBlock.buildingBlocksToDom(buildingBlocks, options);

                const { htmlTemplate, textTemplate } = this.isEmailCreation
                    ? BuildingBlockEmails.buildingBlocksToHtml(buildingBlocks)
                    : {};

                return {
                    ...layout,
                    ...(this.isEmailCreation && { htmlTemplate, textTemplate }),
                    buildingBlocks,
                    dom,
                };
            }

            return layout;
        },
        generatePreview (layout) {
            this.loading = true;

            if (layout.previewHTML) { // some layouts like ai layouts already have a previewHTML, we don't need to process them again
                this.loading = false;

                return layout;
            }

            const buildNodeFromJSON = get(window, 'pendo.buildNodeFromJSON');
            const createBBTooltip = get(window, 'pendo.BuildingBlocks.BuildingBlockTooltips.createBBTooltip');

            const dom = Array.isArray(layout.dom) ? layout.dom[0] : layout.dom;

            normalizePreview(dom);

            const clonedLayout = {
                ...layout,
                previewHTML: BuildingBlock.generatePreviewHTML(buildNodeFromJSON, createBBTooltip, dom),
            };

            // mobile carousel guide should have a unique thumbnail
            if (BuildingBlockLayouts.isCarouselLayout(clonedLayout)) {
                clonedLayout.previewHTML = `
                <div style="width: 126px; height: 112px; display: flex">
                    <img src='${carouselThumbnail}' alt=''/>
                </div>
                `;
            }

            this.loading = false;

            return clonedLayout;
        },
        processLayout (layout) {
            const layoutWithBuildingBlockProperties = this.generateBuildingBlockProperties(layout);
            const layoutWithPreview = this.generatePreview(layoutWithBuildingBlockProperties);

            return layoutWithPreview;
        },
    },
};
</script>

<style lang="less" scoped>
    .pendo-loading-parent--relative {
        top: 50px;
    }
</style>
